import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { playerGameStatusLabels } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import {
  findThresholdColor,
  maxScoresBySport,
  thresholdsBySport,
} from '@sorare/core/src/lib/thresholds';
import type { Color } from '@sorare/core/src/style/types';

import type { ScoresBreakdown_averageLastScore } from './__generated__/index.graphql.ts';

type Props = {
  averageLastScore: Nullable<ScoresBreakdown_averageLastScore>;
  sport?: Sport;
};

const DNP = 'DNP' as const;

const Root = styled(Vertical)`
  padding: var(--double-unit);
  background-color: var(--c-nd-50);
  border-radius: var(--double-unit);
  border: 1px solid var(--color-nd-150);
`;

const ProgressBar = styled(Horizontal)`
  background-color: var(--c-nd-100);
  height: var(--triple-unit);
  gap: 2px;
  border-radius: var(--unit);
`;

const ProgressBarItem = styled.div`
  height: var(--triple-unit);
  transition: all 0.3s;
  &:first-child {
    border-top-left-radius: var(--unit);
    border-bottom-left-radius: var(--unit);
  }
  &:last-child {
    border-top-right-radius: var(--unit);
    border-bottom-right-radius: var(--unit);
  }
`;

const Bullet = styled.div`
  width: var(--unit);
  height: var(--unit);
  border-radius: 50%;
`;
const Label = styled(Horizontal)`
  flex-grow: 1;
`;

const StrongText = styled(BrandS).attrs({ as: 'span' })`
  color: var(--c-white);
`;

const BaselineHorizontal = styled(Horizontal).attrs({ gap: 0.5 })`
  align-items: baseline;
`;

const ListItem = ({
  color,
  count,
  children,
}: {
  color: Color;
  count: number;
  children: ReactNode;
}) => {
  return (
    <Horizontal>
      <Label>
        <Bullet style={{ background: color }} />
        <LabelM color="var(--c-nd-600)">
          <BaselineHorizontal>{children}</BaselineHorizontal>
        </LabelM>
      </Label>
      <LabelL bold>{count}</LabelL>
    </Horizontal>
  );
};
export const ScoresBreakdown = ({
  averageLastScore,
  sport = Sport.FOOTBALL,
}: Props) => {
  if (!averageLastScore || !isType(averageLastScore, 'AverageLastScore'))
    return null;

  const totalScores = averageLastScore?.lastIndividualScores.length || 0;

  const thresholdScoreMaxAndLevel = thresholdsBySport[sport];
  const firstThresholdScore = thresholdScoreMaxAndLevel[0][0];
  const maxScore = maxScoresBySport[sport];

  const scoresCountByColor = (
    averageLastScore?.lastIndividualScores || []
  ).reduce<{ [K in Color | typeof DNP]?: number }>((acc, score) => {
    if (score) {
      const { color } = findThresholdColor(score, sport);
      acc[color] = (acc[color] || 0) + 1;
    } else {
      acc[DNP] = (acc[DNP] || 0) + 1;
    }
    return acc;
  }, {});

  const ranges = [
    [0, firstThresholdScore - 1],
    ...thresholdScoreMaxAndLevel.map(([score], index) => {
      const nextScore = thresholdScoreMaxAndLevel[index + 1]?.[0] || maxScore;
      return [score, nextScore - 1];
    }),
    [maxScore, Infinity],
  ].map(([scoreMin, scoreMax]) => {
    const { color } = findThresholdColor(scoreMin + 1, sport);
    return {
      scoreMin,
      scoreMax,
      color,
      count: scoresCountByColor[color] || 0,
    };
  });
  const rangesDesc = [...ranges].reverse();

  const rangeDNP = {
    scoreMin: 0,
    color: 'var(--c-nd-150)' as Color,
    count: scoresCountByColor[DNP] || 0,
  };
  return (
    <Root>
      <ProgressBar>
        {[rangeDNP, ...ranges].map(
          ({ scoreMin, color, count }) =>
            count > 0 && (
              <ProgressBarItem
                key={scoreMin}
                style={{
                  background: color,
                  width: `${(count / totalScores) * 100}%`,
                }}
              />
            )
        )}
      </ProgressBar>
      <Vertical>
        {rangesDesc.map(({ scoreMin, scoreMax, color, count }) => {
          return (
            <ListItem key={scoreMin} color={color} count={count}>
              {scoreMax === Infinity ? (
                <StrongText>{Math.round(scoreMin)}</StrongText>
              ) : (
                <FormattedMessage
                  id="ScoresBreakdown.label"
                  defaultMessage="{min} to {max}"
                  values={{
                    min: <StrongText>{Math.round(scoreMin)}</StrongText>,
                    max: <StrongText>{Math.round(scoreMax)}</StrongText>,
                  }}
                />
              )}
            </ListItem>
          );
        })}
        <ListItem color={rangeDNP.color} count={rangeDNP.count}>
          <StrongText>
            <FormattedMessage {...playerGameStatusLabels.did_not_play_short} />
          </StrongText>
          <FormattedMessage {...playerGameStatusLabels.did_not_play} />
        </ListItem>
      </Vertical>
    </Root>
  );
};

ScoresBreakdown.fragments = {
  averageLastScore: gql`
    fragment ScoresBreakdown_averageLastScore on AverageLastScoreInterface {
      id
      ... on AverageLastScore {
        id
        lastIndividualScores
      }
    }
  ` as TypedDocumentNode<ScoresBreakdown_averageLastScore>,
};
